import React, { useState, useEffect } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { Alert } from '@material-ui/lab';
import { Button } from '@material-ui/core';

// ========================================
// CONFIGURACIÓN CENTRALIZADA DE ADMIN
// Solo modifica aquí para agregar/quitar admins
// ========================================

/** Emails de administradores */
export const ADMIN_EMAILS = [
  'admin@pichincha.com', 
  'fvochoa@pichincha.com',
  'emieses@pichincha.com'
];

/** Grupos de Azure AD que tienen permisos de administrador */
export const ADMIN_GROUPS = [
  'group:default/admin',
  'group:default/admins',
  'group:default/gca_azu_cross_devops_architects',
  'group:default/gca_azu_eid_aks_cig_cross_devops',
  'group:default/gca_azu_ado_cig_backstage_devops',
];

/** Usuarios específicos por userEntityRef (respaldo) */
export const ADMIN_USER_REFS = [
  'fvochoa',
  'emieses'
];

// ========================================
// HOOK PRINCIPAL: useIsAdmin
// ========================================

/**
 * Hook para verificar si el usuario actual es administrador
 * Usa la configuración centralizada de ADMIN_EMAILS, ADMIN_GROUPS y ADMIN_USER_REFS
 */
export const useIsAdmin = () => {
  const identityApi = useApi(identityApiRef);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const profileInfo = await identityApi.getProfileInfo();
        
        const userEmail = profileInfo?.email || '';
        
        // Debug: Información del usuario
        console.log('Admin Check Debug:', {
          userEntityRef: identity.userEntityRef,
          userEmail: userEmail,
          ownershipEntityRefs: identity.ownershipEntityRefs,
          adminConfig: { ADMIN_EMAILS, ADMIN_GROUPS, ADMIN_USER_REFS }
        });
        
        // 1. Verificar por email
        let userIsAdmin = ADMIN_EMAILS.some(adminEmail => 
          userEmail.toLowerCase() === adminEmail.toLowerCase()
        );
        
        // 2. Verificar por userEntityRef (respaldo)
        if (!userIsAdmin) {
          userIsAdmin = ADMIN_USER_REFS.some(ref => 
            identity.userEntityRef.includes(ref)
          );
        }
        
        // 3. Verificar por grupos
        if (!userIsAdmin && identity.ownershipEntityRefs) {
          userIsAdmin = identity.ownershipEntityRefs.some(ref => 
            ADMIN_GROUPS.some(adminGroup => ref.includes(adminGroup))
          );
        }

        console.log('Admin Status Result:', { userIsAdmin, identity: identity.userEntityRef });
        
        setIsAdmin(userIsAdmin);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [identityApi]);

  return { isAdmin, loading, error };
};

// ========================================
// COMPONENTES QUE USAN useIsAdmin
// ========================================

type AdminOnlyComponentProps = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
};

/**
 * Componente que protege funciones administrativas y solo muestra el contenido
 * si el usuario actual es un administrador.
 * Usa el hook useIsAdmin internamente para mantener consistencia.
 */
export const AdminOnlyComponent: React.FC<AdminOnlyComponentProps> = ({
  children,
  fallback,
}) => {
  const { isAdmin, loading, error } = useIsAdmin();

  if (loading) {
    return <div>Verificando permisos...</div>;
  }

  if (error) {
    return (
      <Alert severity="error">
        Error al verificar permisos: {error.message}
      </Alert>
    );
  }

  if (!isAdmin) {
    return (
      fallback || (
        <Alert severity="warning">
          No tienes permisos para acceder a esta funcionalidad. 
          Contacta al administrador si necesitas acceso.
        </Alert>
      )
    );
  }

  return <>{children}</>;
};

/**
 * Botón que solo es visible para administradores
 */
export const AdminOnlyButton: React.FC<React.ComponentProps<typeof Button>> = (props) => {
  const { isAdmin, loading } = useIsAdmin();

  if (loading || !isAdmin) {
    return null;
  }

  return <Button {...props} />;
};
